/*
 * Copyright (c) 2021. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import z from 'zod';
import { objectKeys } from '../utils';
import { essOperatingEnvelopeSchema, } from './battery';
import { capexSpendScheduleListSchema } from './capex-spend-schedule';
import { formNewConfigPayload, } from './config';
import { componentIdSchema } from './core';
import { hvCostCategoryLabelMap, hvCostingSchema, } from './hv-costing';
export const capexUnitOptions = [
    'costs_per_kwh',
    'costs_per_kw',
    'fixed_costs',
];
// order matters here
export const essCostModelCategoryLabelMap = {
    battery: 'Battery',
    pcs: 'PCS',
    hv: 'HV',
    // HV v5 cost database categories — reuse the HV label map (mirror backend LiIonCostCategory)
    ...hvCostCategoryLabelMap,
    project_substation: 'Project Substation',
    epc: 'EPC',
    ems: 'EMS',
};
export const augmentationTypes = ['Initial System', 'Inverter Addition'];
export const probabilityOfExceedances = ['P10', 'P50', 'P90'];
const baseCostModelItemSchema = z.object({
    item: z.string().optional(),
    // renamed from `item`; legacy key still accepted by the backend on input
    name: z.string(),
    category: z.enum(objectKeys(essCostModelCategoryLabelMap)),
    discountable: z.boolean().optional(),
    ess_only: z.boolean().optional(),
    augmentation_types: z.array(z.enum(augmentationTypes)).nullable().optional(),
    notes: z.string().optional().nullable(),
    // flat $ added on top of the per-unit variable cost (renamed from `fixed_cost`)
    // negative values allowed (e.g. cost credits / discounts) — see PA-4189
    fixed: z.number().optional(),
});
const bomItemSchema = z.object({
    ...baseCostModelItemSchema.shape,
    id: z.uuid(),
    // negative values allowed (e.g. cost credits / discounts) — see PA-4189
    price: z.number().optional(),
    // per-unit cost rate (renamed from `price`)
    variable: z.number(),
    discretionary: z.boolean().optional(),
});
const augmentationSchema = z.object({
    year: z.number().int().min(0),
    type: z.string(),
    energy_add: z.number().min(0),
    ac_energy_desired_rating: z.number().min(0).nullable(),
});
const billOfMaterialsSchema = z.object({
    costs_per_kwh: z.array(bomItemSchema),
    costs_per_kw: z.array(bomItemSchema),
    fixed_costs: z.array(bomItemSchema),
    // structured HV v5 cost model (per-driver lists + discretionary), mirroring
    // PVCosting; v2–v4 HV costs live as flat fixed_costs items instead
    hv: hvCostingSchema.nullable().optional(),
});
const lossesSchema = z.object({
    lv_line: z.number().min(0).max(0.1),
    inverter: z.number().min(0).max(0.1),
    mv_transformer: z.number().min(0).max(0.1),
    mv_ac_line: z.number().min(0).max(0.1),
    txm_loss: z.number().min(0).max(0.1),
    aux_loss: z.number().min(0).max(0.1),
    other_losses: z.number().min(-1).max(1),
    x_trans_hv: z.number().min(0).max(1).optional(),
    x_trans_mv: z.number().min(0).max(1).optional(),
});
const thermoAuxSchema = z.object({
    hvac_seer_air_cooled: z.number().min(0).optional(),
    hvac_seer_liquid_cooled: z.number().min(0).optional(),
    ambient_cooling_load_btu_per_250_sqft: z.number().min(0).optional(),
});
const dataSheetAuxSchema = z.object({
    substation_control_room_mw: z.number().min(0).optional(),
    aux_safety_margin: z.number().gte(0).optional(),
});
// Main power transformer config. Shared by the auxiliary-loads model and the
// HV v5 cost model (by_mpt_count / by_mva_capacity drivers). Saved ESS configs
// keep complete values so they can also run independently of a paired PV workflow.
const mptSchema = z
    .object({
    number: z.number().int().gte(1),
    rating_kva: z.number().gt(0),
    no_load_loss_kw: z.number().gte(0),
    load_loss_kw: z.number().gte(0),
})
    .check((ctx) => {
    const { rating_kva, no_load_loss_kw, load_loss_kw } = ctx.value;
    if (no_load_loss_kw + load_loss_kw > rating_kva) {
        ctx.issues.push({
            code: 'custom',
            message: 'Combined MPT no-load and load loss must not exceed transformer rating',
            path: ['load_loss_kw'],
            input: load_loss_kw,
        });
    }
});
const auxLoadsSchema = z.object({
    annual_perc_throughput: z.number().min(0).optional().nullable(),
    thermo_model: thermoAuxSchema.optional().nullable(),
    data_sheet_model: dataSheetAuxSchema.optional().nullable(),
    include_idle_loads: z.boolean().optional(),
    include_operating_loads: z.boolean().optional(),
});
const baseEssCostingSimulationRequestSchema = z.object({
    project: z.object({
        cod: z.iso.datetime(),
        contracted_term: z.number().int().min(0),
        merchant_tail_term: z.number().int().min(0),
        ac_power_rating: z.number().min(0),
        ac_energy_rating: z.number().min(0),
        power_factor: z.number().min(0).max(1),
        miles_to_ic: z.number().min(0),
        interconnection_size: z.number().min(0),
        interconnection_voltage: z.number().min(0),
        pv_size: z.number().min(0),
        design_temperature: z.number().min(-50).max(100),
        technology: z.enum(['li_ion', 'h2es']),
        max_battery_blocks_per_pcs: z.number().nullable().optional(),
        max_battery_blocks_per_mvt: z.number().int().gt(0).nullable().optional(),
    }),
    costs: z.object({
        ems_license_per_kwh: z.number().min(0),
        om_cost_per_kwh: z.number().min(0),
        vol_discount: z.number().min(-1).max(1),
        capacity_maintenance: z.boolean(),
        augmentations: z.array(augmentationSchema),
        contingency: z.number().min(-1).max(1),
        epc_integrator_margin: z.number().min(0).max(0.5),
        om_cost_escalator: z.number().min(0).max(0.2),
        misc_om_cost_per_kwh: z.number().min(0).optional(),
        purchase_cost_per_acre: z.number().min(0),
        annual_lease_rate: z.number().min(0),
        equipment_spend_schedule: capexSpendScheduleListSchema.nullable().optional(),
        epc_spend_schedule: capexSpendScheduleListSchema.nullable().optional(),
    }),
    ess_op_envelope: essOperatingEnvelopeSchema.extend({
        dispatch_schedule: z.string().optional(),
    }),
    battery: componentIdSchema,
    inverter: componentIdSchema.nullable().optional(),
    mvt: componentIdSchema.nullable().optional(),
    mpt: mptSchema,
    databases: z.object({
        cost_model: componentIdSchema.nullable(),
        hv_cost_database: componentIdSchema.nullable().optional(),
    }),
    boms: z.array(billOfMaterialsSchema),
    losses: lossesSchema.optional(),
    auxiliary_loads: auxLoadsSchema.nullable().optional(),
    weather: componentIdSchema.nullable().optional(),
    simulation: z
        .object({
        p10_adj: z.number().min(-0.5).max(0),
        p90_adj: z.number().min(0).max(0.5),
    })
        .optional(),
});
const essCostingSimulationRequestSchema = baseEssCostingSimulationRequestSchema;
export const essCostOutputsSchema = z.strictObject({
    cost: z.number().min(0),
    cost_per_kwh: z.number().min(0),
});
const baseSimulationSchema = baseEssCostingSimulationRequestSchema.omit({
    battery: true,
    inverter: true,
    mvt: true,
    databases: true,
});
export const newEssConfigPayload = formNewConfigPayload(essCostingSimulationRequestSchema);
export const envelopeLabel = (option) => `${option?.c_rate}C, ${option?.cycles_per_year} cycles, ${option?.resting_soc * 100}% Rest SoC, ${option?.depth_of_discharge * 100}% DoD`;
export const getPxxValue = (p10Adj, p90Adj) => {
    let pxx = 'P50';
    let pxx_adj = 0;
    if (p10Adj) {
        pxx = 'P10';
        pxx_adj = p10Adj * 100;
    }
    else if (p90Adj) {
        pxx = 'P90';
        pxx_adj = p90Adj * 100;
    }
    return {
        pxx,
        pxx_adj,
    };
};
//# sourceMappingURL=ess-costing.js.map